"use client";

import React, { useState } from "react";
import { Camera, Focus, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTrigger, DialogClose } from "@/components/ui/dialog";
const categories = ["All", "Live Performance", "Studio Sessions", "Tour Life"];
const galleryImages = [{
  id: "img-1",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO12.jpg?v=65yxl50mgat",
  category: "Live Performance",
  title: "Front Row Chaos",
  exif: {
    camera: "Nikon Z9",
    lens: "24-70mm f/2.8 S",
    settings: "f/2.8 • 1/500s • ISO 3200"
  }
}, {
  id: "img-2",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO5.jpg?v=1kzt0i4f3mw",
  category: "Studio Sessions",
  title: "The Valve Glow",
  exif: {
    camera: "Leica SL2",
    lens: "50mm Summilux",
    settings: "f/1.4 • 1/125s • ISO 400"
  }
}, {
  id: "img-3",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO15.jpg?v=j6rpl6w6j6a",
  category: "Tour Life",
  title: "Midnight Highway",
  exif: {
    camera: "Sony A7S III",
    lens: "35mm f/1.4 GM",
    settings: "f/1.4 • 1/60s • ISO 12800"
  }
}, {
  id: "img-4",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO10.jpg?v=86nc1ycnvsi",
  category: "Live Performance",
  title: "Neon Pulse",
  exif: {
    camera: "Canon R3",
    lens: "70-200mm f/2.8L",
    settings: "f/2.8 • 1/800s • ISO 2500"
  }
}, {
  id: "img-5",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/LOGO.jpg?v=b5py6aunf1h",
  category: "Studio Sessions",
  title: "Locked Grooves",
  exif: {
    camera: "Hasselblad X2D",
    lens: "XCD 80mm f/1.9",
    settings: "f/2.0 • 1/200s • ISO 800"
  }
}, {
  id: "img-6",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO14.jpg?v=hy30g96avmh",
  category: "Tour Life",
  title: "Backstage Rituals",
  exif: {
    camera: "Fujifilm X100V",
    lens: "23mm f/2.0",
    settings: "f/2.0 • 1/125s • ISO 1600"
  }
}, {
  id: "img-7",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO3.jpg?v=kt5hw2c7o4i",
  category: "Live Performance",
  title: "The Final Encore",
  exif: {
    camera: "Sony A1",
    lens: "12-24mm f/2.8 GM",
    settings: "f/4.0 • 1/400s • ISO 4000"
  }
}, {
  id: "img-8",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO8.jpg?v=y7o4u81ia1o",
  category: "Studio Sessions",
  title: "Vocal Layering",
  exif: {
    camera: "Canon R5",
    lens: "85mm f/1.2L",
    settings: "f/1.2 • 1/250s • ISO 200"
  }
}, {
  id: "img-9",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO17.jpg?v=ny8o07ztw9",
  category: "Tour Life",
  title: "Concrete Jungle",
  exif: {
    camera: "Ricoh GR III",
    lens: "18.3mm f/2.8",
    settings: "f/5.6 • 1/500s • ISO 400"
  }
}, {
  id: "img-10",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO4.jpg?v=72ist5weyv9",
  category: "Live Performance",
  title: "Crowd Surfing",
  exif: {
    camera: "GoPro Hero 12",
    lens: "Wide",
    settings: "f/2.8 • 1/2000s • ISO 100"
  }
}, {
  id: "img-11",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTOSTAGE.jpg?v=fjqrrrg11jt",
  category: "Studio Sessions",
  title: "Analog Warmth",
  exif: {
    camera: "Pentax 67",
    lens: "105mm f/2.4",
    settings: "f/2.4 • 1/60s • Portra 800"
  }
}, {
  id: "img-12",
  src: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO16.jpg?v=j89q9khlo7l",
  category: "Tour Life",
  title: "Stage Door Exit",
  exif: {
    camera: "Leica M11",
    lens: "35mm f/1.4",
    settings: "f/2.8 • 1/125s • ISO 3200"
  }
}];
function GalleryItem({
  image,
  index
}: {
  image: typeof galleryImages[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <div data-section-id="122316" ref={ref} data-index={index} className={cn("group relative aspect-[4/5] w-full overflow-hidden bg-white/5 rounded-sm transition-all duration-1000 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")} style={{
    transitionDelay: `${index % 4 * 100}ms`
  }}>
      <Dialog>
        <DialogTrigger asChild>
          <button className="h-full w-full cursor-zoom-in outline-none">
            <img src={image.src} alt={image.title} className="h-full w-full object-cover grayscale transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0 group-focus-visible:scale-105 group-focus-visible:grayscale-0" loading="lazy" />
            {}
            <div className="absolute inset-0 bg-red-600/20 opacity-0 transition-opacity duration-500 group-hover:opacity-100 group-focus-visible:opacity-100 flex items-center justify-center">
              <Maximize2 className="size-8 text-white" strokeWidth={1.5} />
            </div>
          </button>
        </DialogTrigger>
        
        <DialogContent showCloseButton={false} className="!fixed !inset-0 !max-w-none !w-screen !h-[100dvh] !translate-x-0 !translate-y-0 !rounded-none !border-none !bg-black/98 backdrop-blur-2xl !p-0 flex flex-col items-center justify-center overflow-hidden">
          <DialogClose className="absolute top-6 right-6 z-50 rounded-sm bg-white/10 p-3 text-white/70 backdrop-blur-md transition-colors hover:bg-red-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-600">
            <span className="sr-only">Close</span>
            <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </DialogClose>

          <div className="relative flex h-full w-full items-center justify-center p-4 md:p-12">
            <img src={image.src} alt={image.title} className="max-h-full max-w-full object-contain shadow-2xl rounded-sm" />
          </div>

          {}
          <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black via-black/60 to-transparent p-6 md:p-10">
            <div className="flex flex-col gap-2 text-white/80">
              <h3 className="font-serif text-2xl text-white md:text-3xl">{image.title}</h3>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-sans text-xs tracking-wider md:text-sm">
                <span className="flex items-center gap-2">
                  <Camera className="size-4 text-red-600" />
                  {image.exif.camera}
                </span>
                <span className="flex items-center gap-2">
                  <Focus className="size-4 text-red-600" />
                  {image.exif.lens}
                </span>
                <span className="text-white/30">|</span>
                <span className="text-white/60">{image.exif.settings}</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}
function GalleryGrid({
  images
}: {
  images: typeof galleryImages;
}) {
  return <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {images.map((image, i) => <GalleryItem key={image.id} image={image} index={i} />)}
    </div>;
}
export default function TheFullGallery() {
  return <section id="visual-access" className="dark bg-black text-white min-h-screen py-24">
      <div className="mx-auto max-w-[100rem] px-4 md:px-8">
        
        <div className="mb-16 flex flex-col items-center justify-between gap-8 md:flex-row md:items-end">
          
        </div>

        <Tabs defaultValue="All" className="w-full">
          <div className="mb-12 overflow-x-auto pb-4 scrollbar-hide">
            <TabsList className="flex h-auto w-max min-w-full justify-start gap-8 rounded-none border-b border-white/10 bg-transparent p-0">
              {categories.map((category, i) => <TabsTrigger key={category} value={category} data-index={i} className="relative rounded-none border-b-2 border-transparent bg-transparent px-0 pb-4 pt-2 font-sans text-xs uppercase font-bold tracking-widest text-white/50 transition-colors hover:text-red-600 data-[state=active]:border-red-600 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none">Gallery</TabsTrigger>)}
            </TabsList>
          </div>

          {categories.map((category, i) => <TabsContent key={category} value={category} data-index={i} className="mt-0 outline-none focus-visible:ring-0">
              <GalleryGrid images={category === "All" ? galleryImages : galleryImages.filter(img => img.category === category)} />
            </TabsContent>)}
        </Tabs>

      </div>
    </section>;
}